/**
 * Custom hook for fetching subscriber statuses on the client-side.
 * @param userUuid - The UUID of the user to fetch statuses for
 * @returns Subscriber status data with loading and error states
 */
'use client';

import useSWR from 'swr';
import { getAuthTokenClient } from '@/utils/helpers';
import { API_URLS, getJSONData, parseErrorMessage } from '@/lib/api/config';

export type SubscriberStatusesData = {
  user_uuid: string;
  aged_out: boolean;
  active: boolean;
  active_count: number;
  summary_status: string;
  jwts: string | null;
};

export interface UseSubscriberStatusesReturn {
  data: SubscriberStatusesData | undefined;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

const fetcher = async (url: string): Promise<SubscriberStatusesData> => {
  const authToken = await getAuthTokenClient();

  const config = {
    method: 'GET',
    headers: {
      Authorization: authToken,
    },
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    const errorMessage = await parseErrorMessage(response);
    throw new Error(
      errorMessage ||
        `Failed to fetch subscriber statuses: ${response.status} ${response.statusText}`,
    );
  }

  return getJSONData(response);
};

/**
 * Hook to fetch and cache subscriber statuses
 * @param userUuid - User UUID to fetch statuses for (set to null/undefined to skip fetching)
 */
export function useSubscriberStatuses(
  userUuid: string | null | undefined,
): UseSubscriberStatusesReturn {
  const url = userUuid
    ? `${API_URLS.SUBSCRIPTION_MANAGER_PROXY}/subscriber_statuses/${userUuid}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<SubscriberStatusesData>(
    url,
    fetcher,
    {
      revalidateOnFocus: true,
      shouldRetryOnError: false,
      dedupingInterval: 300000,
    },
  );

  return {
    data,
    loading: isLoading,
    error: error ?? null,
    refetch: () => mutate(),
  };
}
